import { Contract, MyProfileData, ContractDetails as TContractDetails } from 'cpfr-common';
import { useAnalyticsActionTracker } from '@cp-shared-19/frontend-ui';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import { accordionOpenItemsActions } from '../../contracts-overview/AccordionOpenItemsSlice';
import { BundledProducts } from '../bundled-products';
import { ContractPartiesComponent } from '../contract-parties';
import { FinancialDetailsComponent } from '../financial-details';
import { VehicleDetailsComponent } from '../vehicle-details';
import { changeIbanPath, changePaymentDayPath } from '../../../navigation/paths';
import { Accordion } from '@vwfs-bronson/bronson-react';
import { FinancialDetailsOspComponent } from '../financial-details-osp';
import { useLocation } from 'react-router-dom';

export const ContractDetailsUi: React.FC<{
    contract?: Contract & TContractDetails;
    isSMECustomer?: boolean;
    isContactInformationChangePending: boolean;
    myProfileData?: MyProfileData;
    setShowSuccessNotification: (val: boolean) => void;
}> = ({ contract, isSMECustomer, isContactInformationChangePending, myProfileData, setShowSuccessNotification }) => {
    const dispatch = useDispatch();
    const EMPTY_OPEN_ITEMS: string[] = useMemo(() => [], []);
    const openItems = useSelector(
        (state: RootState) => state.accordionOpenItems.openItems[contract?.contractId ?? ''] ?? EMPTY_OPEN_ITEMS,
    );
    const setOpenItems = useCallback(
        (items: string[]) => {
            if (contract?.contractId) {
                dispatch(accordionOpenItemsActions.setOpenItems({ contractId: contract.contractId, items }));
            }
        },
        [dispatch, contract?.contractId],
    );
    const { onAction } = useAnalyticsActionTracker('onOpenContractAccordion');
    const { t } = useTranslation('contracts');
    const ALLOWED_PRODUCT_TYPES = [
        'SENN_PR',
        'SEPO_PR',
        'SENO_PR',
        'SEGN_FL',
        'SMAN_FL',
        'SVDA_FL',
        'SEPN_PR',
        'SEGN_PR',
    ];

    const location = useLocation();

    useEffect(() => {
        if (!contract) return;
        const pathname = location.pathname;
        const openFinancialAccordion =
            pathname.includes(changeIbanPath(contract.contractId)) ||
            pathname.includes(changePaymentDayPath(contract.contractId));
        if (openFinancialAccordion) {
            const isOsp = contract.productType && ALLOWED_PRODUCT_TYPES.includes(contract.productType);
            const financialAccordionId = isOsp
                ? `financial-details-osp-${contract.contractId}`
                : `financial-details-${contract.contractId}`;
            dispatch(
                accordionOpenItemsActions.addOpenItem({ contractId: contract.contractId, item: financialAccordionId }),
            );
        }
    }, [location.pathname, contract?.contractId, contract?.productType, dispatch]);

    const openItemsCopy = useMemo(() => [...openItems], [openItems]);

    if (!contract) {
        return null;
    }

    const { financial, vehicle, contractParties, bundledProducts } = contract;

    const getViewChange = (itemKey: string) => {
        if (itemKey.startsWith('financial-details-osp-') || itemKey === t('financial-details-osp:title')) {
            return 'Financial Details OSP';
        }
        if (
            itemKey.startsWith('financial-details-') ||
            itemKey === t('financial-details:title.private') ||
            itemKey === t('financial-details:title.sme')
        ) {
            return 'Financial details';
        }
        if (itemKey.startsWith('vehicle-details-') || itemKey === t('vehicle-details:title')) {
            return 'Vehicle details';
        }
        if (itemKey.startsWith('contract-parties-') || itemKey === t('contract-parties:title')) {
            return 'Contract parties';
        }
        if (itemKey.startsWith('bundled-products-') || itemKey === t('bundled-products:title')) {
            return 'Products / Services included';
        }
    };

    const handleOnChange = (refs: string[]): void => {
        if (openItems.length > refs.length || !refs.length) {
            setOpenItems([...refs]);
            return;
        }
        setOpenItems([...refs]);
        const openedItem = refs.find((ref) => !openItems.includes(ref)) || refs[refs.length - 1];
        const viewChange = getViewChange(openedItem);
        onAction(viewChange);
    };

    return (
        <Accordion onChange={handleOnChange} openItems={openItemsCopy} multipleOpen>
            <FinancialDetailsOspComponent
                contract={contract}
                financialDetails={financial}
                isContactInformationChangePending={isContactInformationChangePending}
                setShowSuccessNotification={setShowSuccessNotification}
                allowedProductTypes={ALLOWED_PRODUCT_TYPES}
            />
            <FinancialDetailsComponent
                contract={contract}
                financialDetails={financial}
                isContactInformationChangePending={isContactInformationChangePending}
                setShowSuccessNotification={setShowSuccessNotification}
            />
            <VehicleDetailsComponent contract={contract} vehicleDetails={vehicle} isSMECustomer={isSMECustomer} />
            <ContractPartiesComponent contractParties={contractParties} contractId={contract.contractId} />
            <BundledProducts
                contract={contract}
                isSMECustomer={isSMECustomer}
                bundledProductsDetails={bundledProducts}
                contractMileage={financial?.contractMileage}
                myProfileData={myProfileData}
            />
        </Accordion>
    );
};
